import {
    answerSlot,
    findingPhotoSlot,
    multiCountSlot,
    type AnswerRecord,
    type FindingRecord,
    type InspectionRecord,
    type MultiCountRecord,
    type OrderRecord,
    type PhotoRecord,
} from './records';
import type {
    WireAnswer,
    WireFinding,
    WireInspection,
    WireMultiCount,
    WireOrder,
    WirePhoto,
} from './wire';

/**
 * Van draadvorm naar record, voor alles wat van de server komt (bootstrap,
 * pull en de `server_state` van een conflict). Wat van de server komt is per
 * definitie bij de server bekend: `confirmed = 1`.
 */

/** De natuurlijke sleutels in de outbox. Eén sleutel = één ding dat de inspecteur wijzigt. */
export const entityKeys = {
    inspection: (inspectionUuid: string) => `inspection:${inspectionUuid}`,
    answer: (inspectionUuid: string, slot: string) => `answer:${inspectionUuid}:${slot}`,
    multiCount: (inspectionUuid: string, slot: string) => `multi:${inspectionUuid}:${slot}`,
    finding: (findingUuid: string) => `finding:${findingUuid}`,
    order: (orderUuid: string) => `order:${orderUuid}`,
} as const;

export function orderRecordFrom(order: WireOrder): OrderRecord {
    return {
        uuid: order.uuid,
        number: order.number,
        status: order.status,
        scheduledAt: order.scheduled_at,
        scheduledEndAt: order.scheduled_end_at,
        serviceCode: order.service?.code ?? null,
        serviceLabel: order.service?.label ?? null,
        address: order.address
            ? {
                  street: order.address.street,
                  houseNumber: order.address.house_number,
                  houseNumberSuffix: order.address.house_number_suffix,
                  postcode: order.address.postcode,
                  city: order.address.city,
                  bagBuildYear: order.address.bag_build_year ?? null,
              }
            : null,
        clientName: order.client?.name ?? null,
        clientPhone: order.client?.phone ?? null,
        clientEmail: order.client?.email ?? null,
        inspectionUuid: order.inspection_uuid,
        ...(order.assigned_user_uuid === undefined
            ? {}
            : { assigneeUuid: order.assigned_user_uuid }),
    };
}

export function inspectionRecordFrom(
    inspection: Pick<
        WireInspection,
        | 'uuid'
        | 'order_uuid'
        | 'status'
        | 'template_version_uuid'
        | 'started_at'
        | 'completed_at'
        | 'notes'
    >,
    local?: InspectionRecord,
): InspectionRecord {
    return {
        uuid: inspection.uuid,
        orderUuid: inspection.order_uuid,
        status: inspection.status,
        templateVersionUuid: inspection.template_version_uuid,
        startedAt: inspection.started_at,
        completedAt: inspection.completed_at,
        notes: inspection.notes,
        drafts: local?.drafts ?? {},
        clientUpdatedAt: local?.clientUpdatedAt ?? null,
        /* Heropend door de eigenaar: het moment van indienen hoort er niet meer bij. */
        submittedAt: inspection.status === 'submitted' ? (local?.submittedAt ?? null) : null,
    };
}

export function answerRecordFrom(inspectionUuid: string, answer: WireAnswer): AnswerRecord {
    return {
        answerUuid: answer.answer_uuid,
        inspectionUuid,
        questionId: answer.question_id,
        multiScope: answer.multi_scope,
        slot: answerSlot(answer.question_id, answer.multi_scope),
        optionIds: answer.option_ids,
        valueNumber: answer.value_number,
        valueText: answer.value_text,
        reportText: answer.report_text,
        location: answer.location,
        directCost: answer.direct_cost,
        termCost: answer.term_cost,
        improvementCost: answer.improvement_cost,
        costGroupId: answer.cost_group_id,
        urgency: answer.urgency,
        nhgClassification: answer.nhg_classification,
        clientUpdatedAt: answer.client_updated_at,
        confirmed: 1,
    };
}

export function multiCountRecordFrom(
    inspectionUuid: string,
    count: WireMultiCount,
): MultiCountRecord {
    return {
        inspectionUuid,
        slot: multiCountSlot(count.container_node_id, count.multi_scope),
        containerNodeId: count.container_node_id,
        multiScope: count.multi_scope,
        count: count.count,
        clientUpdatedAt: count.client_updated_at,
    };
}

export function findingRecordFrom(inspectionUuid: string, finding: WireFinding): FindingRecord {
    return {
        findingUuid: finding.finding_uuid,
        inspectionUuid,
        nodeId: finding.node_id,
        multiScope: finding.multi_scope,
        title: finding.title,
        description: finding.description,
        location: finding.location,
        severity: finding.severity,
        urgency: finding.urgency,
        directCost: finding.direct_cost,
        termCost: finding.term_cost,
        improvementCost: finding.improvement_cost,
        costGroupId: finding.cost_group_id,
        nhgClassification: finding.nhg_classification,
        source: finding.source,
        clientUpdatedAt: finding.client_updated_at,
        confirmed: 1,
    };
}

/**
 * Een foto die al op de server staat. Hij hangt aan een antwoord; zonder dat
 * antwoord weet het apparaat niet bij welke vraag hij hoort en blijft hij weg.
 */
export function photoRecordFrom(
    inspectionUuid: string,
    photo: WirePhoto,
    answer: Pick<AnswerRecord, 'answerUuid' | 'questionId' | 'multiScope' | 'slot'>,
    local: PhotoRecord | undefined,
    now: number,
): PhotoRecord {
    return {
        clientUuid: photo.client_uuid,
        inspectionUuid,
        questionId: answer.questionId,
        multiScope: answer.multiScope,
        slot: answer.slot,
        answerUuid: answer.answerUuid,
        blob: local?.blob ?? null,
        mimeType: local?.mimeType ?? 'image/jpeg',
        fileName: local?.fileName ?? `${photo.client_uuid}.jpg`,
        byteSize: local?.byteSize ?? 0,
        caption: photo.caption ?? local?.caption ?? null,
        takenAt: photo.taken_at ?? local?.takenAt ?? new Date(now).toISOString(),
        state: 'uploaded',
        attempts: 0,
        nextAttemptAt: 0,
        lastError: null,
        urls: photo.urls ? { thumb: photo.urls.thumb ?? null, web: photo.urls.web ?? null } : null,
        createdAt: local?.createdAt ?? now,
        mediaUuid: photo.uuid ?? local?.mediaUuid ?? null,
        deleted: local?.deleted ?? 0,
        findingUuid: null,
    };
}

/** Een foto van een handmatige bevinding die al op de server staat. */
export function findingPhotoRecordFrom(
    inspectionUuid: string,
    photo: WirePhoto & { finding_uuid: string },
    local: PhotoRecord | undefined,
    now: number,
): PhotoRecord {
    return {
        ...photoRecordFrom(
            inspectionUuid,
            photo,
            {
                answerUuid: '',
                questionId: 0,
                multiScope: [],
                slot: findingPhotoSlot(photo.finding_uuid),
            },
            local,
            now,
        ),
        answerUuid: null,
        findingUuid: photo.finding_uuid,
    };
}
